import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { KeyRound, Shield, Eye, EyeOff, Loader2, X } from 'lucide-react';

interface Props {
  forced?: boolean;   // true = 首次登录强制改密（不可关闭）
  onClose?: () => void; // 主动修改密码时的关闭回调
}

export default function ChangePasswordModal({ forced = true, onClose }: Props) {
  const { changePassword, setSecurityQuestion, user } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 安全问题（可选，推荐设置）
  const [secQuestion, setSecQuestion] = useState('');
  const [secAnswer, setSecAnswer] = useState('');
  const [showSecuritySection, setShowSecuritySection] = useState(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPwd) {
      setError('两次输入的新密码不一致');
      return;
    }
    if (newPassword.length < 6) {
      setError('新密码长度不能少于6位');
      return;
    }
    if (newPassword === oldPassword) {
      setError('新密码不能与旧密码相同');
      return;
    }

    setLoading(true);
    try {
      await changePassword(oldPassword, newPassword);

      // 如果填了安全问题，一并设置
      if (secQuestion && secAnswer) {
        await setSecurityQuestion(secQuestion, secAnswer);
      }

      // 非强制模式：修改成功后关闭弹窗
      if (!forced && onClose) {
        onClose();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '修改失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className={`${forced ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-blue-600 to-indigo-600'} px-6 py-5 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <KeyRound className="w-6 h-6" />
              <h2 className="text-lg font-bold">{forced ? '首次登录 — 请修改默认密码' : '修改密码'}</h2>
            </div>
            {!forced && onClose && (
              <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20 transition-colors">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className={`${forced ? 'text-amber-100' : 'text-blue-100'} text-sm mt-1`}>
            {forced
              ? `${user?.displayName}，为了账号安全，请设置你自己的密码`
              : `${user?.displayName}，请输入当前密码和新密码`}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">当前密码</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={oldPassword}
                onChange={e => setOldPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 outline-none pr-10"
                placeholder={forced ? '默认密码: 985211' : '请输入当前密码'}
                autoComplete="current-password"
                required
              />
              <button type="button" className="absolute right-3 top-2.5 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
              placeholder="至少6位"
              autoComplete="new-password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
            <input
              type="password"
              value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
              autoComplete="new-password"
              required
            />
          </div>

          {/* 安全问题推荐 */}
          <div className="border-t pt-3">
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setShowSecuritySection(!showSecuritySection)}
            >
              <Shield className="w-4 h-4" />
              {showSecuritySection ? '收起' : '设置'}安全问题（推荐，用于忘记密码时自助重置）
            </button>

            {showSecuritySection && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">安全问题</label>
                  <select
                    value={secQuestion}
                    onChange={e => setSecQuestion(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">请选择安全问题（可选）</option>
                    <option>你的宠物叫什么名字?</option>
                    <option>你最喜欢的老师是谁?</option>
                    <option>你的生日是几月几号?</option>
                    <option>你小学的名字是?</option>
                    <option>你最好的朋友叫什么?</option>
                  </select>
                </div>
                {secQuestion && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">答案</label>
                    <input
                      type="text"
                      value={secAnswer}
                      onChange={e => setSecAnswer(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="请输入答案"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {loading ? '修改中...' : '确认修改'}
          </button>
        </form>
      </div>
    </div>
  );
}
